"use client"

import { Popover, Transition } from "@headlessui/react"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { Bars3Icon } from "@modules/common/icons"
import { Text, clx, useToggleState } from "@medusajs/ui"
import { Fragment } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import CountrySelect from "../country-select"
import type { HttpTypes } from "@medusajs/types"

const MENU_ITEMS = [
  { name: "AOV Roof Vents",                href: "/categories/aov-roof-vent" },
  { name: "Smoke Louvres",                 href: "/categories/aov-louvre" },
  { name: "Smoke Dampers",                href: "/categories/aov-damper" },
  { name: "Smoke Fans",                    href: "/categories/aov-fan" },
  { name: "AOV Windows",                   href: "/categories/aov-window" },
  { name: "AOV Smoke Controls",            href: "/categories/aov-controls" },
  { name: "AOV Smoke Control Accessories", href: "/categories/aov-smoke-control-accessories" },
  { name: "About Us",                      href: "/about-us" },
  { name: "Knowledge Hub",                href: "/aov-knowledge-hub" },
  { name: "Contact Us",                    href: "/contact-aov" },
]

const SideMenu = ({
  regions,
}: {
  regions: HttpTypes.StoreRegion[] | null
}) => {
  const toggleState = useToggleState()
  const menuItems = MENU_ITEMS

  return (
    <div className="h-full">
      <div className="flex h-full items-center">
        <Popover className="flex h-full">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  aria-label="Open menu"
                  className="relative flex h-full items-center p-2 text-aov-navy transition-all duration-200 ease-out hover:text-aov-orange focus:outline-none"
                >
                  <Bars3Icon className="h-6 w-6" />
                </Popover.Button>
              </div>

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <Popover.Panel className="fixed inset-x-0 top-0 z-[60] m-2 flex h-[calc(100vh-1rem)] w-full flex-col pr-4 text-sm text-white backdrop-blur-2xl sm:left-auto sm:min-w-min sm:w-1/3 sm:pr-0 2xl:w-1/4">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex h-full flex-col justify-between rounded-lg bg-aov-navy-dark/95 p-6"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button
                        data-testid="close-menu-button"
                        aria-label="Close menu"
                        onClick={close}
                        className="text-white transition-colors hover:text-aov-orange"
                      >
                        <XMark />
                      </button>
                    </div>
                    <ul className="flex flex-col items-start justify-start gap-4">
                      {menuItems.map(({ name, href }) => (
                        <li key={`${name}-${href}`}>
                          <LocalizedClientLink
                            href={href}
                            className="font-display text-2xl font-bold leading-10 transition-colors hover:text-aov-orange"
                            onClick={close}
                            data-testid={`${name.toLowerCase().replace(/\s+/g, "-")}-link`}
                          >
                            {name}
                          </LocalizedClientLink>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-col gap-y-6">
                      <div
                        className="flex justify-between"
                        onMouseEnter={toggleState.open}
                        onMouseLeave={toggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={toggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            toggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text className="txt-compact-small flex justify-between text-white/50">
                        © {new Date().getFullYear()} AOV Direct. All rights
                        reserved.
                      </Text>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
